import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { agruparPorDepartamento, fmtQtd } from '../utils/especificacao';
import './EspecificacaoPdf.css';

// Tudo pensado em folha A4 (210 × 297 mm). As páginas são montadas AQUI, medindo
// cada bloco — a tela mostra exatamente as páginas que vão sair no PDF.
const MM = 96 / 25.4;                   // px por mm (unidade CSS)
const A4_LARGURA = 210;                 // mm
const MARGEM = 12;                      // mm (topo, laterais e base)
const RODAPE = 9;                       // mm reservados pro "Página X de Y"
const UTIL = (297 - 2 * MARGEM - RODAPE - 1) * MM; // altura útil por página (px), 1 mm de folga p/ arredondamento

/**
 * Quantitativo pronto pra imprimir/salvar em PDF (formato do exemplo que os
 * arquitetos já mandam pro cliente): categoria → itens com foto, descrição,
 * quantidade e "Para: ambientes". Logo do escritório no topo. Sem preço.
 * "Baixar PDF" abre a impressão do navegador (Salvar como PDF) — sem servidor
 * gerando arquivo. No celular a folha A4 só encolhe; o layout é sempre o mesmo.
 */
export default function EspecificacaoPdf() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiUrl, user } = useAuth();
  const [esp, setEsp] = useState(null);
  const [logo, setLogo] = useState(null);
  const [erro, setErro] = useState('');
  const [paginas, setPaginas] = useState(null); // [[bloco, ...], ...]
  const [escala, setEscala] = useState(1);
  const [semFoto, setSemFoto] = useState({}); // itens cuja foto não carregou (cadastro sem arquivo)
  const medida = useRef(null);

  useEffect(() => {
    Promise.all([api.get(`/especificacoes/${id}`), api.get('/especificacoes/logo').catch(() => ({ data: {} }))])
      .then(([e, l]) => { setEsp(e.data); setLogo(l.data.logo || null); })
      .catch(e => setErro(e.response?.data?.error || 'Não foi possível abrir a especificação'));
  }, [id]);

  // Nome do arquivo sugerido na hora de salvar = título da página
  useEffect(() => {
    if (!esp) return undefined;
    const antes = document.title;
    document.title = `Quantitativo - ${esp.nome}`;
    return () => { document.title = antes; };
  }, [esp]);

  // Tela estreita (celular): a folha A4 inteira encolhe pra caber — nada se reorganiza
  useEffect(() => {
    const ajustar = () => setEscala(Math.min(1, (window.innerWidth - 24) / (A4_LARGURA * MM)));
    ajustar();
    window.addEventListener('resize', ajustar);
    return () => window.removeEventListener('resize', ajustar);
  }, []);

  const grupos = esp ? agruparPorDepartamento(esp.itens) : [];
  const hoje = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
  const fotoDe = (i) => (i.foto && !semFoto[i.id] ? (i.livre ? i.foto : `${apiUrl}${i.foto}`) : null);

  // Paginação: mede cada bloco (fora da tela, na largura útil da A4) e distribui
  // nas páginas sem cortar item ao meio. Título de categoria nunca fica sozinho no
  // pé da página, e categoria que continua na página seguinte repete o título.
  const paginar = useCallback(() => {
    const raiz = medida.current;
    if (!raiz || !esp) return;
    const h = (chave) => raiz.querySelector(`[data-b="${chave}"]`)?.getBoundingClientRect().height || 0;
    const hMini = h('mini'), hSec = h('sec');
    const pags = [];
    let atual = [{ tipo: 'cab' }];
    let usado = h('cab');
    const quebrar = () => { pags.push(atual); atual = [{ tipo: 'mini' }]; usado = hMini; };
    const cabe = (alt) => usado + alt <= UTIL;
    const soCabecalho = () => atual.every(b => b.tipo === 'cab' || b.tipo === 'mini');

    grupos.forEach((g, gi) => {
      const primeiro = g.itens[0] ? h(`i${g.itens[0].id}`) : 0;
      if (!cabe(hSec + primeiro) && !soCabecalho()) quebrar();
      atual.push({ tipo: 'sec', nome: g.nome, gi }); usado += hSec;
      g.itens.forEach((it, ii) => {
        const alt = h(`i${it.id}`);
        if (!cabe(alt) && ii > 0) { quebrar(); atual.push({ tipo: 'sec', nome: g.nome, gi, cont: true }); usado += hSec; }
        atual.push({ tipo: 'item', it }); usado += alt;
      });
    });
    if (esp.observacao) {
      const alt = h('obs');
      if (!cabe(alt) && !soCabecalho()) quebrar();
      atual.push({ tipo: 'obs' }); usado += alt;
    }
    const altNota = h('nota');
    if (!cabe(altNota) && !soCabecalho()) quebrar();
    atual.push({ tipo: 'nota' });
    pags.push(atual);
    setPaginas(pags);
  }, [esp, grupos]);

  // Mede depois que as fontes carregam (a largura do texto muda a quebra de linha)
  useEffect(() => {
    if (!esp) return;
    let vivo = true;
    // setTimeout (e não requestAnimationFrame): rAF não roda com a aba em segundo plano
    (document.fonts?.ready || Promise.resolve()).then(() => { if (vivo) setTimeout(paginar, 0); });
    return () => { vivo = false; };
  }, [esp, logo]);

  const baixar = async () => {
    window.print();
    if (esp.status !== 'finalizada') {
      try { await api.put(`/especificacoes/${esp.id}`, { status: 'finalizada' }); } catch { /* segue */ }
    }
  };

  if (erro) return <div className="pdf-msg">{erro}</div>;
  if (!esp) return <div className="pdf-msg"><div className="spinner-border" style={{ color: '#1F6A2D' }} /></div>;

  // ---- Blocos (os mesmos na medição e nas páginas). Funções simples, não
  // componentes: declarados aqui dentro, componentes remontariam a cada render. ----
  const cabecalho = (k) => (
    <div key={k} className="pdf-bloco">
      <header className="pdf-cab">
        <div className="pdf-cab-arq">
          {logo ? <img src={logo} alt="" /> : <span className="pdf-arq-nome">{user?.nome}</span>}
        </div>
        <div className="pdf-cab-data">{hoje}</div>
      </header>
      <div className="pdf-titulo">
        <div className="pdf-rot">Quantitativo</div>
        <h1>{esp.nome}</h1>
        {(esp.cliente_nome || esp.cliente_contato) && (
          <div className="pdf-sub">
            {esp.cliente_nome && <span><b>Cliente:</b> {esp.cliente_nome}</span>}
            {esp.cliente_contato && <span>{esp.cliente_contato}</span>}
          </div>
        )}
      </div>
    </div>
  );
  const cabecalhoMini = (k) => (
    <div key={k} className="pdf-bloco">
      <div className="pdf-mini">
        <div className="pdf-mini-arq">{logo ? <img src={logo} alt="" /> : <span>{user?.nome}</span>}</div>
        <div className="pdf-mini-tit">Quantitativo · <strong>{esp.nome}</strong></div>
      </div>
    </div>
  );
  const secao = (nome, cont, k) => (
    <div key={k} className="pdf-bloco"><h2 className="pdf-sec-tit">{nome}{cont && <span> (continuação)</span>}</h2></div>
  );
  const item = (it, k) => (
    <div key={k} className="pdf-bloco">
      <div className="pdf-item">
        <div className="pdf-foto">
          {/* Foto que não carrega vira o quadro vazio (sem o ícone de imagem quebrada no PDF) */}
          {fotoDe(it) ? <img src={fotoDe(it)} alt="" onError={() => setSemFoto(s => ({ ...s, [it.id]: true }))} /> : <span />}
        </div>
        <div className="pdf-desc">
          <div className="pdf-nome">{it.nome}</div>
          <div className="pdf-det">
            {it.marca && <span><b>Marca:</b> {it.marca}</span>}
            {it.medida && <span><b>Medida:</b> {it.medida}</span>}
            {it.codpro && <span><b>Cód. Centrão:</b> {it.codpro}</span>}
          </div>
          {it.ambientes.length > 0 && <div className="pdf-para"><b>Para:</b> {it.ambientes.join(', ')}</div>}
          {it.observacao && <div className="pdf-obs">{it.observacao}</div>}
        </div>
        <div className="pdf-qtd">
          <span>Quantidade</span>
          <strong>{fmtQtd(it.quantidade, it.unidade)}</strong>
        </div>
      </div>
    </div>
  );
  const observacoes = (k) => (
    <div key={k} className="pdf-bloco pdf-obs-geral">
      <h2 className="pdf-sec-tit">Observações</h2>
      <p>{esp.observacao}</p>
    </div>
  );
  const nota = (k) => (
    <div key={k} className="pdf-bloco">
      <p className="pdf-nota">* Quantidades líquidas — não contêm a margem de perda (recortes e quebras).</p>
    </div>
  );
  const bloco = (b, k) => {
    if (b.tipo === 'cab') return cabecalho(k);
    if (b.tipo === 'mini') return cabecalhoMini(k);
    if (b.tipo === 'sec') return secao(b.nome, b.cont, k);
    if (b.tipo === 'item') return item(b.it, k);
    if (b.tipo === 'obs') return observacoes(k);
    return nota(k);
  };

  return (
    <div className="pdf-tela">
      <div className="pdf-barra">
        <button type="button" className="pdf-voltar" onClick={() => navigate(`/especificacoes/${esp.id}`)}><i className="bi bi-chevron-left" />Voltar</button>
        <span className="pdf-dica">
          {paginas ? `${paginas.length} ${paginas.length === 1 ? 'página' : 'páginas'} A4 · ` : ''}na janela que abrir, escolha <b>“Salvar como PDF”</b>.
        </span>
        <button type="button" className="pdf-baixar" onClick={baixar} disabled={!paginas}><i className="bi bi-download" />Baixar PDF</button>
      </div>

      {/* Medição (invisível, na largura útil da A4) — base da paginação */}
      <div ref={medida} className="pdf-medida" aria-hidden="true">
        <div data-b="cab">{cabecalho()}</div>
        <div data-b="mini">{cabecalhoMini()}</div>
        <div data-b="sec">{secao('Categoria')}</div>
        {esp.itens.map(it => <div key={it.id} data-b={`i${it.id}`}>{item(it)}</div>)}
        {esp.observacao && <div data-b="obs">{observacoes()}</div>}
        <div data-b="nota">{nota()}</div>
      </div>

      <div className="pdf-paginas" style={{ zoom: escala }}>
        {!paginas && <div className="pdf-msg"><div className="spinner-border" style={{ color: '#1F6A2D' }} /></div>}
        {paginas && paginas.map((p, pi) => (
          <section key={pi} className="pdf-pagina">
            <div className="pdf-conteudo">{p.map((b, bi) => bloco(b, bi))}</div>
            <footer className="pdf-pe">
              <span>Quantitativo · {esp.nome}</span>
              <span>Página {pi + 1} de {paginas.length}</span>
            </footer>
          </section>
        ))}
      </div>
    </div>
  );
}
